/**
 * AdminGuard Component
 *
 * ADMIN MENU = CONDITIONAL_CAPABILITY_DRIVEN. The admin area is open to a
 * caller only when the SERVER reports at least one admin function for them
 * (GET /capabilities → admin.functions), computed with the same predicates
 * the admin routes enforce: Central's platform-admin claim (`centralAdmin`)
 * plus each route's capability. A workspace role label (owner / admin) is NOT
 * the authority — every admin screen here edits or reads global data.
 *
 * With `fn`, the guard opens one admin screen only when the caller holds THAT
 * function: holding one admin function never implies the others. The server
 * still enforces every route independently; this only avoids dead screens.
 *
 * - Mounted inside AuthGuard → BootGate (App.tsx); never waits on a boot it
 *   cannot see: until auth + boot are settled it renders the login redirect
 *   or BootGate itself (useGuardPrerequisites), so a direct load resolves
 * - Unknown (capabilities not loaded / failed) never opens the area
 * - The area with no admin functions is a clear "no admin functions" state
 *
 * ── PRECEDENCE: a terminal auth state outranks every waiting state ────────
 *
 *   1. SESSION_EXPIRED / SESSION_REVOKED / AUTH_REQUIRED → Central login
 *   2. WORKSPACE_CLOSED            → workspace-closed UI   (BootGate)
 *   3. APP_ACCESS_DENIED           → Access Denied         (BootGate)
 *   4. CENTRAL_UNAVAILABLE         → Retry Connection      (BootGate)
 *   5. a genuine authenticated data failure → the page's own error
 *
 * That ordering is NOT optional and it is NOT re-implemented here: it is the
 * whole contract of `useGuardPrerequisites`, which answers the auth question
 * (`<Navigate to="/login">`) BEFORE it waits on `bootCompleted`. The guard
 * used to ask in the opposite order, and that inversion is why every /admin
 * route sat on "Loading…" for ever: when the session ends at the boot
 * perimeter, `applyBootFailure` clears auth AND sets bootStatus to
 * 'unauthenticated', so `bootCompleted` is false — and boot can never
 * complete, because it has no token to boot with. "The session is over" is a
 * FACT, not a pending state, so it is answered first.
 *
 * Everything below `if (pending)` therefore runs only on a live, booted
 * session, and nothing below can mask a terminal verdict.
 */

import { Outlet } from 'react-router-dom';
import { RouteLoading, useGuardPrerequisites } from './guardPrerequisites';
import { useGetCapabilitiesQuery, type AdminFunctionKey } from '../../store/api/capabilitiesApi';
import { adminAccessAllows } from './adminAccess';

// The pure predicates live in ./adminAccess (no store/auth imports); re-exported for existing callers.
export { adminAccessAllows, adminFunctionsFrom } from './adminAccess';

interface AdminGuardProps {
  /** Require this specific admin function (else: any admin function). */
  fn?: AdminFunctionKey;
  children?: React.ReactNode;
}

export function AdminGuard({ fn, children }: AdminGuardProps) {
  const pending = useGuardPrerequisites();
  const { data: caps, isLoading: capsLoading, isError } = useGetCapabilitiesQuery(undefined, { skip: pending !== null });

  // Auth and boot first — a terminal verdict is answered before any wait.
  if (pending) return pending;
  if (capsLoading) return <RouteLoading />;

  if (!adminAccessAllows(caps, fn)) {
    // The area itself (no fn) with no admin functions: say exactly that.
    const title = isError
      ? 'Admin functions unavailable'
      : fn
        ? 'Access Denied'
        : 'No admin functions';
    const body = isError
      ? 'Your admin functions could not be loaded. Try again in a moment.'
      : fn
        ? "You don't have permission to access this page. Admin functions require a Central platform administrator with the matching capability."
        : 'Your account has no BeHeard admin functions. Admin functions require a Central platform administrator with the matching capability.';
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="admin-access-denied">
        <div className="text-center max-w-md mx-auto p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
          <p className="text-gray-600 mb-6">{body}</p>
          <a
            href="/dashboard"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Go to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return <>{children ?? <Outlet />}</>;
}
